import type { Ref } from "vue";
import type { Requests } from "../common/request.common";
import type { NetworkOLTResponseTypes } from "../common/response.common";
import type { SelectHook } from "../common/types.common";
import { asUpdateHook, get, post, postEmpty } from "./common";

export const networkOLT = {
    record(data: typeof Requests.NetworkOLT.Record.Input) {
        return post<typeof data, NetworkOLTResponseTypes.Record>(`/network-olt/record`, data);
    },
    create(data: typeof Requests.NetworkOLT.Create.Input) {
        return post<typeof data>(`/network-olt/create`, data);
    },
    obtain(id: string) {
        return get<NetworkOLTResponseTypes.Obtain>(`/network-olt/obtain/${id}`);
    },
    delete(id: string) {
        return postEmpty(`/network-olt/delete/${id}`);
    },
    refresh(id: string) {
        return postEmpty(`/network-olt/refresh/${id}`);
    },
    setup(id: string) {
        return postEmpty(`/network-olt/setup/${id}`);
    },
    fetchFrames(id: string) {
        return postEmpty<NetworkOLTResponseTypes.FetchFrames>(`/network-olt/frame/${id}/fetch`);
    },
    obtainFrame(id: string, data: typeof Requests.NetworkOLT.ObtainFrame.Input) {
        return post<typeof data, NetworkOLTResponseTypes.ObtainFrame>(
            `/network-olt/frame/${id}/obtain`,
            data,
        );
    },
    toggleDLPortAutofind(id: string, data: typeof Requests.NetworkOLT.ToggleDLPortAutofind.Input) {
        return post<typeof data, NetworkOLTResponseTypes.ToggleDLPortAutofind>(
            `/network-olt/frame/${id}/update/autofind`,
            data,
        );
    },
    fetchDBAProfiles(id: string) {
        return postEmpty<NetworkOLTResponseTypes.FetchDBAProfiles>(`/network-olt/dba/${id}/fetch`);
    },
    obtainDBAProfile(id: string, data: typeof Requests.NetworkOLT.ObtainDBAProfile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.ObtainDBAProfile>(
            `/network-olt/dba/${id}/obtain`,
            data,
        );
    },
    createDBAProfile(id: string, data: typeof Requests.NetworkOLT.CreateDBAProfile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.CreateDBAProfile>(
            `/network-olt/dba/${id}/create`,
            data,
        );
    },
    deleteDBAProfile(id: string, data: typeof Requests.NetworkOLT.DeleteDBAProfile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.DeleteDBAProfile>(
            `/network-olt/dba/${id}/delete`,
            data,
        );
    },
    fetchSRVProfiles(id: string) {
        return postEmpty<NetworkOLTResponseTypes.FetchSRVProfiles>(`/network-olt/srv/${id}/fetch`);
    },
    obtainSRVProfile(id: string, data: typeof Requests.NetworkOLT.ObtainSRVProfile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.ObtainSRVProfile>(
            `/network-olt/srv/${id}/obtain`,
            data,
        );
    },
    createSRVProfile(id: string, data: typeof Requests.NetworkOLT.CreateSRVProfile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.CreateSRVProfile>(
            `/network-olt/srv/${id}/create`,
            data,
        );
    },
    deleteSRVProfile(id: string, data: typeof Requests.NetworkOLT.DeleteSRVProfile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.DeleteSRVProfile>(
            `/network-olt/srv/${id}/delete`,
            data,
        );
    },
    fetchLineProfiles(id: string) {
        return postEmpty<NetworkOLTResponseTypes.FetchLineProfiles>(
            `/network-olt/line/${id}/fetch`,
        );
    },
    obtainLineProfile(id: string, data: typeof Requests.NetworkOLT.ObtainLineProfile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.ObtainLineProfile>(
            `/network-olt/line/${id}/obtain`,
            data,
        );
    },
    createLineProfile(id: string, data: typeof Requests.NetworkOLT.CreateLineProfile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.CreateLineProfile>(
            `/network-olt/line/${id}/create`,
            data,
        );
    },
    deleteLineProfile(id: string, data: typeof Requests.NetworkOLT.DeleteLineProfile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.DeleteLineProfile>(
            `/network-olt/line/${id}/delete`,
            data,
        );
    },
    fetchTR069Profiles(id: string) {
        return postEmpty<NetworkOLTResponseTypes.FetchTR069Profiles>(
            `/network-olt/tr069/${id}/fetch`,
        );
    },
    obtainTR069Profile(id: string, data: typeof Requests.NetworkOLT.ObtainTR069Profile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.ObtainTR069Profile>(
            `/network-olt/tr069/${id}/obtain`,
            data,
        );
    },
    createTR069Profile(id: string, data: typeof Requests.NetworkOLT.CreateTR069Profile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.CreateTR069Profile>(
            `/network-olt/tr069/${id}/create`,
            data,
        );
    },
    deleteTR069Profile(id: string, data: typeof Requests.NetworkOLT.DeleteTR069Profile.Input) {
        return post<typeof data, NetworkOLTResponseTypes.DeleteTR069Profile>(
            `/network-olt/tr069/${id}/delete`,
            data,
        );
    },
    fetchVLANs(id: string) {
        return postEmpty<NetworkOLTResponseTypes.FetchVLANs>(`/network-olt/vlan/${id}/fetch`);
    },
    obtainVLAN(id: string, data: typeof Requests.NetworkOLT.ObtainVLAN.Input) {
        return post<typeof data, NetworkOLTResponseTypes.ObtainVLAN>(
            `/network-olt/vlan/${id}/obtain`,
            data,
        );
    },
    createVLAN(id: string, data: typeof Requests.NetworkOLT.CreateVLAN.Input) {
        return post<typeof data, NetworkOLTResponseTypes.CreateVLAN>(
            `/network-olt/vlan/${id}/create`,
            data,
        );
    },
    updateVLANAttachPort(id: string, data: typeof Requests.NetworkOLT.UpdateVLANAttachPort.Input) {
        return post<typeof data, NetworkOLTResponseTypes.UpdateVLANAttachPort>(
            `/network-olt/vlan/${id}/update/attach`,
            data,
        );
    },
    updateVLANDetachPort(id: string, data: typeof Requests.NetworkOLT.UpdateVLANDetachPort.Input) {
        return post<typeof data, NetworkOLTResponseTypes.UpdateVLANDetachPort>(
            `/network-olt/vlan/${id}/update/detach`,
            data,
        );
    },
    deleteVLAN(id: string, data: typeof Requests.NetworkOLT.DeleteVLAN.Input) {
        return post<typeof data, NetworkOLTResponseTypes.DeleteVLAN>(
            `/network-olt/vlan/${id}/delete`,
            data,
        );
    },
    probeONTs(id: string) {
        return postEmpty<NetworkOLTResponseTypes.ProbeONTs>(`/network-olt/ont/${id}/probe`);
    },
    fetchONTs(id: string) {
        return postEmpty<NetworkOLTResponseTypes.FetchONTs>(`/network-olt/ont/${id}/fetch`);
    },
    obtainONT(id: string, data: typeof Requests.NetworkOLT.ObtainONT.Input) {
        return post<typeof data, NetworkOLTResponseTypes.ObtainONT>(
            `/network-olt/ont/${id}/obtain`,
            data,
        );
    },
    createONT(id: string, data: typeof Requests.NetworkOLT.CreateONT.Input) {
        return post<typeof data, NetworkOLTResponseTypes.CreateONT>(
            `/network-olt/ont/${id}/create`,
            data,
        );
    },
    updateONTAttachVLAN(id: string, data: typeof Requests.NetworkOLT.UpdateONTAttachVLAN.Input) {
        return post<typeof data, NetworkOLTResponseTypes.UpdateONTAttachVLAN>(
            `/network-olt/ont/${id}/update/vlan/attach`,
            data,
        );
    },
    updateONTDetachVLAN(id: string, data: typeof Requests.NetworkOLT.UpdateONTDetachVLAN.Input) {
        return post<typeof data, NetworkOLTResponseTypes.UpdateONTDetachVLAN>(
            `/network-olt/ont/${id}/update/vlan/detach`,
            data,
        );
    },
    updateONTCreateTR069WAN(
        id: string,
        data: typeof Requests.NetworkOLT.UpdateONTCreateTR069WAN.Input,
    ) {
        return post<typeof data, NetworkOLTResponseTypes.UpdateONTCreateTR069WAN>(
            `/network-olt/ont/${id}/update/wan/create`,
            data,
        );
    },
    updateONTDeleteTR069WAN(
        id: string,
        data: typeof Requests.NetworkOLT.UpdateONTDeleteTR069WAN.Input,
    ) {
        return post<typeof data, NetworkOLTResponseTypes.UpdateONTDeleteTR069WAN>(
            `/network-olt/ont/${id}/update/wan/delete`,
            data,
        );
    },
    deleteONT(id: string, data: typeof Requests.NetworkOLT.DeleteONT.Input) {
        return post<typeof data, NetworkOLTResponseTypes.DeleteONT>(
            `/network-olt/ont/${id}/delete`,
            data,
        );
    },
    rebootONT(id: string, data: typeof Requests.NetworkOLT.RebootONT.Input) {
        return post<typeof data, NetworkOLTResponseTypes.RebootONT>(
            `/network-olt/ont/${id}/reboot`,
            data,
        );
    },
    formHooks: {
        select: (id: string) => {
            return (async () => {
                const req = get<NetworkOLTResponseTypes.SelectVLAN>(
                    `/network-olt/vlan/${id}/select`,
                );

                return (await req.execute()) ?? [];
            }) satisfies SelectHook;
        },
        update(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.NetworkOLT.Update.Input) => {
                return await asUpdateHook(
                    post<typeof data>(`/network-olt/update/${id}`, data),
                    isLoading,
                );
            };
        },
    },
};
